import { logger } from '@/lib/logger';

/**
 * Page Store Helper Functions (Legacy)
 * 
 * 既存の実装との後方互換性を保つためのヘルパー関数群
 */

import { subscribe, snapshot } from 'valtio';
import type { PageStore, PageState, PageAction, PageTreeNode } from '../types';

// WeakMapベースのメモ化ユーティリティ
const weakMemoize = <T extends object, R>(fn: (arg: T) => R): ((arg: T) => R) => {
  const cache = new WeakMap<T, R>();
  return (arg: T): R => {
    if (cache.has(arg)) {
      return cache.get(arg) as R;
    }
    const result = fn(arg);
    cache.set(arg, result);
    return result;
  };
};

// グローバル参照（初期化時に設定）
let _pageStore: PageStore | null = null;
let _dispatchPage: ((action: PageAction) => void) | null = null;

// 初期化関数
export function initializeHelpers(pageStore: PageStore, dispatchPage: (action: PageAction) => void): void {
  _pageStore = pageStore;
  _dispatchPage = dispatchPage;
}

// PageStoreへのアクセサ
function getPageStore(): PageStore {
  if (!_pageStore) {
    throw new Error('pageStoreHelpers not initialized. Call initializeHelpers first.');
  }
  return _pageStore;
}

// DispatchPageへのアクセサ
function getDispatchPage(): (action: PageAction) => void {
  if (!_dispatchPage) {
    throw new Error('pageStoreHelpers not initialized. Call initializeHelpers first.');
  }
  return _dispatchPage;
}

// 子ページの再帰的削除
function _deleteChildPages(parentId: string): void {
  const pageStore = getPageStore();
  Object.values(pageStore.pages).forEach(page => {
    if (page.parentId === parentId) {
      delete pageStore.pages[page.id];
      pageStore.pageOrder = pageStore.pageOrder.filter(id => id !== page.id);
      _deleteChildPages(page.id);
    }
  });
}

// スナップショットの型定義
type PageStoreSnapshot = {
  readonly pageOrder: readonly string[];
  readonly pages: {
    readonly [key: string]: {
      readonly id: string;
      readonly title: string;
      readonly content: string;
      readonly parentId: string | null;
      readonly order_index?: number;
      readonly createdAt: Date;
      readonly updatedAt: Date;
      readonly isDeleted: boolean;
      readonly blocks?: readonly unknown[];
    };
  };
  readonly editingPages: ReadonlySet<string>;
  readonly expandedPages: ReadonlySet<string>;
  readonly isLoading: boolean;
  readonly error: string | null;
};

// ソート済みページリストのメモ化（スナップショットごとにキャッシュ）
const memoizedSortedPages = weakMemoize((snap: PageStoreSnapshot) => {
  return [...snap.pageOrder]
    .map(id => snap.pages[id])
    .filter(page => page && !page.isDeleted)
    .map(page => ({
      ...page,
      blocks: page.blocks ? [...page.blocks] : undefined
    })) as PageState[];
});

// ページツリー構造のメモ化
const memoizedPageTree = weakMemoize((snap: PageStoreSnapshot) => {
  // pageOrderを使用してページを処理
  const rootPages = snap.pageOrder
    .map((id: string) => snap.pages[id])
    .filter((page) => {
      if (!page) return false;
      return (page.parentId === null || page.parentId === undefined) && !page.isDeleted; // 削除済みを除外
    })
    .sort((a, b) => {
      // order_indexでソート
      const orderA = a.order_index || 0;
      const orderB = b.order_index || 0;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      // order_indexが同じ場合はcreatedAtでソート
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    });
  
  // Convert readonly pages to mutable PageState for tree building
  const mutablePages = rootPages.map(page => ({
    ...page,
    blocks: page.blocks ? [...page.blocks] : undefined
  })) as PageState[];
  
  const pagesRecord = Object.fromEntries(
    Object.entries(snap.pages).map(([id, page]) => [
      id,
      {
        ...page,
        blocks: page.blocks ? [...page.blocks] : undefined
      } as PageState
    ])
  );
  
  return mutablePages.map(page => buildTreeNode(page, pagesRecord));
});

// 派生状態の計算関数
export const pageDerived = {
  // ソート済みページリスト
  sortedPages: (): Array<PageStore['pages'][string]> => {
    const snap = snapshot(getPageStore()) as PageStoreSnapshot;
    return memoizedSortedPages(snap);
  },
  
  // ページツリー構造
  pageTree: (): PageTreeNode[] => {
    const snap = snapshot(getPageStore()) as PageStoreSnapshot;
    return memoizedPageTree(snap);
  },
  
  // 未保存の変更があるか
  hasUnsavedChanges: (): boolean => {
    const snap = snapshot(getPageStore());
    return snap.editingPages.size > 0;
  }
};

// ツリーノードの構築
function buildTreeNode(page: PageState, pages: Record<string, PageState>): PageTreeNode {
  const children = Object.values(pages)
    .filter(p => p.parentId === page.id && !p.isDeleted)
    .sort((a, b) => {
      const orderA = a.order_index || 0;
      const orderB = b.order_index || 0;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    })
    .map(child => buildTreeNode(child, pages));
  
  return { page, children };
}

// 既存のヘルパー関数との互換性を保つための関数
export function buildPageTree(pages: PageState[], parentId: string | null = null): PageTreeNode[] {
  return pages
    .filter((page: PageState) => page.parentId === parentId)
    .map((page: PageState) => ({
      page,
      children: buildPageTree(pages, page.id),
    }));
}

// 後方互換性のための追加エクスポート
export const togglePageExpansion = (pageId: string): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'TOGGLE_PAGE_EXPANSION', pageId });
};

export const expandPage = (pageId: string): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'EXPAND_PAGE', pageId });
};

export const collapsePage = (pageId: string): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'COLLAPSE_PAGE', pageId });
};

export const addPage = (page: PageState): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'ADD_PAGE', page });
};

export const updatePage = (pageId: string, updates: Partial<PageState>): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'UPDATE_PAGE', pageId, updates });
};

export const deletePage = (pageId: string): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'DELETE_PAGE', pageId });
};

export const setPageOrder = (order: string[]): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'SET_PAGE_ORDER', order });
};

export const startEditing = (pageId: string): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'START_EDITING', pageId });
};

export const stopEditing = (pageId: string): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'STOP_EDITING', pageId });
};

export const setLoading = (isLoading: boolean): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'SET_LOADING', isLoading });
};

export const setError = (error: string | null): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'SET_ERROR', error });
};

export const clearError = (): void => {
  const dispatch = getDispatchPage();
  dispatch({ type: 'CLEAR_ERROR' });
};

// 購読ユーティリティ
export function subscribeToPageStore(callback: () => void): () => void {
  return subscribe(getPageStore(), callback);
}

// 追加のエクスポート（pageStore.tsで使用）
export function getPagePath(pageId: string): PageState[] {
  const pageStore = getPageStore();
  const path: PageState[] = [];
  let currentPageId: string | null = pageId;
  
  while (currentPageId) {
    const page = pageStore.pages[currentPageId];
    if (!page) break;
    path.unshift(page);
    currentPageId = page.parentId;
  }
  
  return path;
}

export function hasCircularReference(pageId: string, newParentId: string | null): boolean {
  if (!newParentId) return false;
  
  const pageStore = getPageStore();
  let currentId: string | null = newParentId;
  const visited = new Set<string>();
  
  while (currentId) {
    if (currentId === pageId) return true;
    if (visited.has(currentId)) return false;
    visited.add(currentId);
    
    const page = pageStore.pages[currentId];
    if (!page) break;
    currentId = page.parentId;
  }
  
  return false;
}

export function logPageState(): void {
  const pageStore = getPageStore();
  logger.debug('Page State:', snapshot(pageStore));
}

export function clearEditingPages(): void {
  const dispatch = getDispatchPage();
  dispatch({ type: 'CLEAR_EDITING_PAGES' });
}
